import _ from "lodash";
import { getProductData } from "./Product/ProductService/productService";
import { convertToFormattedTime, insurers } from "./formServiceFunctions";
import { EntityBackedHistoryLog, HistoryLog } from "../types/HistoryLog";
import { translate } from "./Translations/translatorService";

import { ProductUnion } from "../types/ProductUnion";
import dayjs from "dayjs";

const getInsurerIdFromIri = (iri: string) => {
  return _.last(iri.split("/"));
};

export const prepareHistory = (
  history: HistoryLog[],
  productClassName: ProductUnion["productClassName"],
): HistoryLog[] => {
  const historyCopy: HistoryLog[] = [];

  // unpack entityDataChange history log entries

  for (const key in history) {
    const historyLog: HistoryLog = _.cloneDeep(history[key]);
    if (historyLog.type !== "entityDataChange") {
      historyCopy.push(historyLog);
      continue;
    }
    if (Object.keys(historyLog.logData).includes("preventAllSystemEmails")) {
      historyCopy.push({ ..._.cloneDeep(historyLog), type: "mailPause" });
      delete historyLog.logData["preventAllSystemEmails"];
    }
    if (Object.keys(historyLog.logData).includes("preventAccounting")) {
      historyCopy.push({ ..._.cloneDeep(historyLog), type: "accountingPause" });
      delete historyLog.logData["preventAccounting"];
    }
    if (Object.keys(historyLog.logData).some((key) => !["preventAccounting", "mailPause"].includes(key))) {
      historyCopy.push(historyLog);
    }
  }
  _.forEach(historyCopy, (historyItem) => {
    _.forEach(historyItem.logData, (logDataEntry: any, key) => {
      if (!logDataEntry || !logDataEntry.current || !logDataEntry.previous) {
        return;
      }
      //convert dates
      if (
        _.find(
          getProductData(productClassName, "formTransformations.dateFields"),
          (dateField) => dateField.path === key,
        )
      ) {
        logDataEntry.current = logDataEntry.current
          ? dayjs(logDataEntry.current).format("DD.MM.YYYY")
          : logDataEntry.current;
        logDataEntry.previous = logDataEntry.previous
          ? dayjs(logDataEntry.previous).format("DD.MM.YYYY")
          : logDataEntry.previous;
      }

      //convert insurances
      //works as long as insurance has this path in all products
      if (key === "caseInsurance.insurer") {
        if (logDataEntry.current) {
          const currentInsurer = _.find(
            insurers,
            (insurer) => insurer.id + "" === getInsurerIdFromIri(logDataEntry.current),
          );
          logDataEntry.current = currentInsurer ? currentInsurer.label : logDataEntry.current;
        }
        if (logDataEntry.previous) {
          const previousInsurer = _.find(
            insurers,
            (insurer) => insurer.id + "" === getInsurerIdFromIri(logDataEntry.previous),
          );
          logDataEntry.previous = previousInsurer ? previousInsurer.label : logDataEntry.previous;
        }
      }

      //convert times
      if (
        _.find(getProductData(productClassName, "formTransformations.timeFields"), (timeField) => timeField === key)
      ) {
        logDataEntry.current = convertToFormattedTime(logDataEntry.current);
        logDataEntry.previous = convertToFormattedTime(logDataEntry.previous);
      }
    });
  });
  return historyCopy;
};

export function cloneHistoryLogAsGeneric<T>(historyLog: T): Omit<T, "type"> & { type: "generic" } {
  return { ...historyLog, type: "generic" };
}

const fallbackSummary = () => translate("historyItem.details.emptySummary");

const isLegacyStylesheetExcerpt = (value: string): boolean => value.trimStart().startsWith("/* What it does:");

const getFaxTargetNumber = (targetNumber: string | null): string => {
  if (!targetNumber) {
    return fallbackSummary();
  }
  if (targetNumber.startsWith("+49")) {
    return targetNumber;
  }

  return `+49${targetNumber.replace(/^(0049|0)/, "")}`;
};

const getInquirySummary = (historyItem: Extract<EntityBackedHistoryLog, { type: "inquiryToCustomer" }>): string => {
  const { inquiryType, identifier } = historyItem.logData.snapshot;
  if (inquiryType === "link") {
    return translate(`historyItem.inquiry.link.identifier.${identifier ?? "unknown"}.label`);
  }

  return inquiryType ? translate(`historyItem.inquiry.type.${inquiryType}`) : fallbackSummary();
};

export const getEntityHistoryTitle = (historyItem: EntityBackedHistoryLog): string => {
  switch (historyItem.type) {
    case "message": {
      const snapshot = historyItem.logData.snapshot;
      return `${translate(`historyItem.actions.message.${snapshot.direction}`)}: ${snapshot.conversationSubject ?? fallbackSummary()}`;
    }
    case "beaMessage": {
      const snapshot = historyItem.logData.snapshot;
      const action = translate(`historyItem.actions.beaMessage.${snapshot.transmissionDirection ?? "unknown"}`);
      const id = snapshot.beaMessageId ?? historyItem.logData.entityId ?? fallbackSummary();
      const subject = snapshot.subject ? ` (${snapshot.subject})` : "";
      const recipient = snapshot.recipientName ? ` an ${snapshot.recipientName}` : "";

      return `${action}: ${id}${subject}${recipient}`;
    }
    case "fax": {
      const snapshot = historyItem.logData.snapshot;
      const targetNumber = getFaxTargetNumber(snapshot.targetNumber);
      const status = snapshot.status ? ` (${translate(`fax.status.values.${snapshot.status}`, snapshot.status)})` : "";

      return `${translate("historyItem.actions.fax")}: an ${targetNumber}${status}`;
    }
    case "mediaObject":
    case "mediaObjectDelete":
    case "mediaObjectIdentifierChange":
      return `${translate(`historyItem.actions.${historyItem.type}`)}: ${historyItem.logData.snapshot.originalName ?? fallbackSummary()}`;
    case "inquiryToCustomer":
      return `${translate("historyItem.actions.inquiryToCustomer")}: ${getInquirySummary(historyItem)}`;
    case "activity":
      return `${translate(`historyItem.actions.activity.${historyItem.logData.snapshot.activityEventType ?? "unknown"}`)}: ${historyItem.logData.snapshot.subject ?? fallbackSummary()}`;
    case "note":
      return `${translate("historyItem.actions.note")}: ${historyItem.logData.snapshot.textExcerpt ?? fallbackSummary()}`;
    case "emailMessage":
      return `${translate(`historyItem.actions.emailMessage.${historyItem.logData.snapshot.direction}`)}: ${historyItem.logData.snapshot.subject ?? fallbackSummary()}`;
    case "paidTask":
      return `${translate("historyItem.actions.paidTask")}: ${historyItem.logData.snapshot.subject ?? fallbackSummary()}`;
  }
};

export const getEntityHistoryPreview = (historyItem: EntityBackedHistoryLog): string | null => {
  switch (historyItem.type) {
    case "message": {
      const excerpt = historyItem.logData.snapshot.textExcerpt;
      return excerpt && isLegacyStylesheetExcerpt(excerpt) ? null : excerpt;
    }
    case "mediaObject":
    case "mediaObjectDelete":
    case "mediaObjectIdentifierChange":
      return historyItem.logData.snapshot.description;
    case "fax":
      return historyItem.logData.snapshot.mediaObjectName;
    default:
      return null;
  }
};
